import React from 'react';
import Modal from './Modal';
import ModalContent from './ModalContent';
import Button from './Button';
import { Heading, Typography } from './Typography';

/**
 * ConfirmModal - A reusable confirmation dialog
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed/cancelled
 * @param {function} onConfirm - Callback when user confirms
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} variant - Variant style: 'danger', 'warning', 'default' (default: 'default')
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  confirmDisabled = false
}) => {
  const handleConfirm = async () => {
    try {
      await Promise.resolve(onConfirm());
      onClose();
    } catch {
      // Don't close on error - let caller handle (e.g. show error message)
    }
  };

  // Map variant to button variant
  const buttonVariant = variant === 'danger' ? 'danger' : variant === 'warning' ? 'warning' : 'primary';

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdropClick={false}>
      <ModalContent size="small" constrainHeight={false}>
        <div className="space-y-4">
          {/* Header */}
          <div>
            <Heading level={3} className="mb-2">{title}</Heading>
            <Typography variant="body" className="text-text-secondary">
              {message}
            </Typography>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
            <Button variant="secondary" onClick={onClose} className="w-full sm:w-auto">
              {cancelText}
            </Button>
            <Button
              variant={buttonVariant}
              onClick={handleConfirm}
              disabled={confirmDisabled}
              className="w-full sm:w-auto"
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmModal;
